import dayjs from 'dayjs';
import { entries, flatten } from 'lodash';
import InputType from 'models/enums/InputType';
import ProductDocumentPropertyPath from 'models/enums/ProductDocumentPropertyPath';
import ProductRefundDocumentPropertyPath from 'models/enums/ProductRefundDocumentPropertyPath';
import ProductTransferOwnershipDocumentPropertyPath from 'models/enums/ProductTransferOwnershipDocumentPropertyPath';
import {
  defaultDateFormat,
  defaultDateTimeFormat,
  defaultServerDateFormat,
  defaultServerDateTimeFormat,
} from '../data/Date';
import { formatNumberWithSeparator, formatNumberWithSeparatorWithoutDecimales } from '../data/Number';

// #region PropertyPathMappings
export const productDocumentPropertyPathTypeMapping: Record<ProductDocumentPropertyPath, InputType> = {
  [ProductDocumentPropertyPath['contract.appNumber']]: InputType.text,
  [ProductDocumentPropertyPath['contract.appNumberWithRevision']]: InputType.text,
  [ProductDocumentPropertyPath['contract.revisionNumber']]: InputType.number,
  [ProductDocumentPropertyPath['contract.creator']]: InputType.text,
  [ProductDocumentPropertyPath['contract.effectiveDate']]: InputType.date,
  [ProductDocumentPropertyPath['contract.expiryDate']]: InputType.date,
  [ProductDocumentPropertyPath['contract.createdAtUtc']]: InputType.date,
  [ProductDocumentPropertyPath['contract.createdAtLocal']]: InputType.date,
  [ProductDocumentPropertyPath['contract.modifiedAtUtc']]: InputType.date,
  [ProductDocumentPropertyPath['contract.termInMonths']]: InputType.number,
  [ProductDocumentPropertyPath['contract.maxKm']]: InputType.number,
  [ProductDocumentPropertyPath['contract.salePriceCAD']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.priceCAD']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.salePriceWithTaxCAD']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.totalTaxCAD']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.dealerCost']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.dealerCostTaxAmt']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.dealerCostTotal']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.dealerCost75Percent']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.dealerProfit']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.dealerProfitTaxAmt']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.coverageKm']]: InputType.number,
  [ProductDocumentPropertyPath['contract.maxCoverageKm']]: InputType.number,
  [ProductDocumentPropertyPath['contract.customInfo']]: InputType.text,
  [ProductDocumentPropertyPath['contract.customInfoGroup']]: InputType.text,
  [ProductDocumentPropertyPath['contract.coApplicantFirstName']]: InputType.text,
  [ProductDocumentPropertyPath['contract.coApplicantLastName']]: InputType.text,
  [ProductDocumentPropertyPath['contract.warrantyOption']]: InputType.text,
  [ProductDocumentPropertyPath['contract.postSale']]: InputType.boolean,
  [ProductDocumentPropertyPath['contract.deductible']]: InputType.number,
  [ProductDocumentPropertyPath['contract.subfees']]: InputType.json,
  [ProductDocumentPropertyPath['contract.vehicle.vin']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.year']]: InputType.number,
  [ProductDocumentPropertyPath['contract.vehicle.makeCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.model']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.bodyType']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.bodyColor']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.posOdometerReading']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.stockNumber']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.engineSize']]: InputType.number,
  [ProductDocumentPropertyPath['contract.vehicle.purchaseDate']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.deliveryDate']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.classDate']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.purchasePrice']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.vehicle.inServiceDate']]: InputType.date,
  [ProductDocumentPropertyPath['contract.vehicle.mfgPowertrainWarrantyMonths']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.mfgPowertrainWarrantyKm']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.mfgWholeVehicleWarrantyMonths']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.mfgWholeVehicleWarrantyKm']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.trimLevel']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.engineType']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.engineInfo']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.driveLine']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.purchaseState']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.purchaseType']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.financedAmount']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.vehicle.residualAmount']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.vehicle.paymentAmount']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.vehicle.numberOfPayments']]: InputType.number,
  [ProductDocumentPropertyPath['contract.vehicle.interestRate']]: InputType.decimal,
  [ProductDocumentPropertyPath['contract.vehicle.paymentCycle']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.id']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.phoneNumber']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.email']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.streetAddress']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.postalCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.muncipality']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.provinceCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.city']]: InputType.text,
  [ProductDocumentPropertyPath['contract.vehicle.lender.countryCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.id']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.primaryPhoneNumber']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.primaryEmailAddress']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.streetAddress']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.postalCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.muncipality']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.city']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.provinceCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.dealer.countryCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.email']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.firstName']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.lastName']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.phone']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.streetAddress']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.postalCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.muncipality']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.provinceCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.countryCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.city']]: InputType.text,
  [ProductDocumentPropertyPath['contract.client.taxExempt']]: InputType.boolean,
  [ProductDocumentPropertyPath['contract.client.taxExemptionBand']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.email']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.firstName']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.lastName']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.phone']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.streetAddress']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.postalCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.muncipality']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.provinceCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.countryCode']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.city']]: InputType.text,
  [ProductDocumentPropertyPath['contract.previousClient.taxExempt']]: InputType.boolean,
  [ProductDocumentPropertyPath['contract.previousClient.taxExemptionBand']]: InputType.text,
  [ProductDocumentPropertyPath['contract.product.code']]: InputType.text,
  [ProductDocumentPropertyPath['contract.product.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.product.productType']]: InputType.text,
  [ProductDocumentPropertyPath['contract.product.canSellPostSale']]: InputType.boolean,
  [ProductDocumentPropertyPath['contract.product.category.code']]: InputType.text,
  [ProductDocumentPropertyPath['contract.product.category.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.product.provider.name']]: InputType.text,
  [ProductDocumentPropertyPath['contract.contractEnhancements']]: InputType.json,
  [ProductDocumentPropertyPath['contract.createdBy.fullName']]: InputType.text,
};
export const productRefundDocumentPropertyPathTypeMapping: Record<ProductRefundDocumentPropertyPath, InputType> = {
  [ProductRefundDocumentPropertyPath['contract.appNumber']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.appNumberWithRevision']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.revisionNumber']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.creator']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.effectiveDate']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contract.expiryDate']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contract.createdAtUtc']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contract.createdAtLocal']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contract.modifiedAtUtc']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contract.termInMonths']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.maxKm']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.salePriceCAD']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.priceCAD']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.salePriceWithTaxCAD']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.totalTaxCAD']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.dealerCost']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.dealerCostTaxAmt']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.dealerCostTotal']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.dealerCost75Percent']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.dealerProfit']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.dealerProfitTaxAmt']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.coverageKm']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.maxCoverageKm']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.customInfo']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.customInfoGroup']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.coApplicantFirstName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.coApplicantLastName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.warrantyOption']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.postSale']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contract.deductible']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.subfees']]: InputType.json,
  [ProductRefundDocumentPropertyPath['contract.vehicle.vin']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.year']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.vehicle.makeCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.model']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.bodyType']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.bodyColor']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.posOdometerReading']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.stockNumber']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.engineSize']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.vehicle.purchaseDate']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.deliveryDate']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.classDate']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.purchasePrice']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.vehicle.inServiceDate']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contract.vehicle.mfgPowertrainWarrantyMonths']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.mfgPowertrainWarrantyKm']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.mfgWholeVehicleWarrantyMonths']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.mfgWholeVehicleWarrantyKm']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.trimLevel']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.engineType']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.engineInfo']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.driveLine']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.purchaseState']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.purchaseType']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.financedAmount']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.vehicle.residualAmount']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.vehicle.paymentAmount']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.vehicle.numberOfPayments']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contract.vehicle.interestRate']]: InputType.decimal,
  [ProductRefundDocumentPropertyPath['contract.vehicle.paymentCycle']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.id']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.phoneNumber']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.email']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.streetAddress']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.postalCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.muncipality']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.provinceCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.city']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.vehicle.lender.countryCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.id']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.primaryPhoneNumber']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.primaryEmailAddress']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.streetAddress']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.postalCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.muncipality']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.city']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.provinceCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.dealer.countryCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.email']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.firstName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.lastName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.phone']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.streetAddress']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.postalCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.muncipality']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.provinceCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.countryCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.city']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.client.taxExempt']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contract.client.taxExemptionBand']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.email']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.firstName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.lastName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.phone']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.streetAddress']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.postalCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.muncipality']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.provinceCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.countryCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.city']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.previousClient.taxExempt']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contract.previousClient.taxExemptionBand']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.product.code']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.product.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.product.productType']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.product.canSellPostSale']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contract.product.category.code']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.product.category.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.product.provider.name']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contract.contractEnhancements']]: InputType.json,
  [ProductRefundDocumentPropertyPath['contract.createdBy.fullName']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.jobId']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.statusCode']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.refundDateAtLocal']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.createdAtUtc']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.modifiedAtUtc']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.isLoyaltyPremiumApplied']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contractRefund.newAppNumber']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.areClaimDeductionsApplied']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contractRefund.totalDeductions']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalDeductionsTax']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalDeductionsTaxAmount']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainFactor']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundFactor']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.termInDays']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.usedTermInDays']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.remainingTermInDays']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.usedTermInDaysPercentage']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.remainingTermInDaysPercentage']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.termInKms']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.usedOdometerReading']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.remainingOdometerReading']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.usedOdometerReadingPercentage']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.remainingOdometerReadingPercentage']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerCostAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerCostTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerCostTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerCostTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerCostTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerCostAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.unprotectedDealerProfitTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.unprotectedDealerProfitTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.unprotectedDealerProfitAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerProfitTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerProfitTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerProfitAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerProfitTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerProfitTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerProfitAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.contractExists']]: InputType.boolean,
  [ProductRefundDocumentPropertyPath['contractRefund.dealerCostTaxAtSale']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.dealerProfitTaxAtSale']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.dealerCostValueAtSale']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.dealerProfitValueAtSale']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retailSalePriceValueAtSale']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retailSalePriceTaxValueAtSale']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalRetainedDealerAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalRetainedDealerTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalRetainedDealerSubtotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalRefundedDealerAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalRefundedDealerTaxAmt']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalRefundedDealerSubtotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.retainedDealerTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.refundedDealerTotal']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.totalToBeRefunded']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.sumOfClaimAmountsPaidOnContract']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.enhancements']]: InputType.json,
  [ProductRefundDocumentPropertyPath['contractRefund.subfees']]: InputType.json,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.type']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.createdAtUtc']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.modifiedAtUtc']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.cancellationDateLocal']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.odometerReading']]: InputType.number,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.reason']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.reasonDescription']]: InputType.text,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.refundDateAtLocal']]: InputType.date,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.refundEnhancements']]: InputType.json,
  [ProductRefundDocumentPropertyPath['contractRefund.contractCancellation.refundSubfees']]: InputType.json,
};
export const productTransferOwnershipDocumentPropertyPathTypeMapping: Record<
  ProductTransferOwnershipDocumentPropertyPath,
  InputType
> = {
  [ProductTransferOwnershipDocumentPropertyPath['contract.appNumber']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.appNumberWithRevision']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.revisionNumber']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.creator']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.effectiveDate']]: InputType.date,
  [ProductTransferOwnershipDocumentPropertyPath['contract.expiryDate']]: InputType.date,
  [ProductTransferOwnershipDocumentPropertyPath['contract.createdAtUtc']]: InputType.date,
  [ProductTransferOwnershipDocumentPropertyPath['contract.createdAtLocal']]: InputType.date,
  [ProductTransferOwnershipDocumentPropertyPath['contract.modifiedAtUtc']]: InputType.date,
  [ProductTransferOwnershipDocumentPropertyPath['contract.termInMonths']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.maxKm']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.salePriceCAD']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.priceCAD']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.salePriceWithTaxCAD']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.totalTaxCAD']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealerCost']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealerCostTaxAmt']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealerCostTotal']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealerCost75Percent']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealerProfit']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealerProfitTaxAmt']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.coverageKm']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.maxCoverageKm']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.customInfo']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.customInfoGroup']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.coApplicantFirstName']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.coApplicantLastName']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.warrantyOption']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.postSale']]: InputType.boolean,
  [ProductTransferOwnershipDocumentPropertyPath['contract.deductible']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.subfees']]: InputType.json,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.vin']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.year']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.makeCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.model']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.bodyType']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.bodyColor']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.posOdometerReading']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.stockNumber']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.engineSize']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.purchaseDate']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.deliveryDate']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.classDate']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.purchasePrice']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.inServiceDate']]: InputType.date,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.mfgPowertrainWarrantyMonths']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.mfgPowertrainWarrantyKm']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.mfgWholeVehicleWarrantyMonths']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.mfgWholeVehicleWarrantyKm']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.trimLevel']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.engineType']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.engineInfo']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.driveLine']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.purchaseState']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.purchaseType']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.financedAmount']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.residualAmount']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.paymentAmount']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.numberOfPayments']]: InputType.number,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.interestRate']]: InputType.decimal,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.paymentCycle']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.id']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.phoneNumber']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.email']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.streetAddress']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.postalCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.muncipality']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.provinceCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.city']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.vehicle.lender.countryCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.id']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.primaryPhoneNumber']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.primaryEmailAddress']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.streetAddress']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.postalCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.muncipality']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.city']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.provinceCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.dealer.countryCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.email']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.firstName']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.lastName']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.phone']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.streetAddress']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.postalCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.muncipality']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.provinceCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.countryCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.city']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.taxExempt']]: InputType.boolean,
  [ProductTransferOwnershipDocumentPropertyPath['contract.previousClient.taxExemptionBand']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.email']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.firstName']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.lastName']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.phone']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.streetAddress']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.postalCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.muncipality']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.provinceCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.countryCode']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.city']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.taxExempt']]: InputType.boolean,
  [ProductTransferOwnershipDocumentPropertyPath['contract.client.taxExemptionBand']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.code']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.productType']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.canSellPostSale']]: InputType.boolean,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.category.code']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.category.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.product.provider.name']]: InputType.text,
  [ProductTransferOwnershipDocumentPropertyPath['contract.contractEnhancements']]: InputType.json,
  [ProductTransferOwnershipDocumentPropertyPath['contract.createdBy.fullName']]: InputType.text,
};
const packagePropertyPaths = [
  ProductDocumentPropertyPath['contract.salePriceCAD'],
  ProductDocumentPropertyPath['contract.totalTaxCAD'],
  ProductDocumentPropertyPath['contract.salePriceWithTaxCAD'],
];
const taxPropertyPaths = [
  ProductDocumentPropertyPath['contract.totalTaxCAD'],
  ProductDocumentPropertyPath['contract.dealerCostTaxAmt'],
  ProductDocumentPropertyPath['contract.dealerProfitTaxAmt'],
];
//#endregion

function convertObjectToKeyValuePairList(obj: object) {
  return entries(obj).map(([k, v]) => `${k}:${v}`);
}
function matchKeyValuePairLabel(value: any = '', inputLabel: string = '') {
  if (typeof value !== 'string') return false;

  const [k1, v1] = value.split(':');
  const [k2, v2] = inputLabel.split(':');

  return k1 === k2 && (v2 === '*' ? !!v1 : v1 === v2);
}
function extractValueFromLabel(label: any) {
  return label?.toString()?.split(':')[1] ?? '';
}
function checkIfValueExists(value: any, inputLabel: string) {
  return Array.isArray(value)
    ? value.some((it) => matchKeyValuePairLabel(it, inputLabel))
    : matchKeyValuePairLabel(value, inputLabel);
}
function getLabelFromValue(value: any, inputLabel: string) {
  return Array.isArray(value)
    ? value.filter((it) => checkIfValueExists(it, inputLabel)).map((it) => extractValueFromLabel(it))
    : matchKeyValuePairLabel(value, inputLabel)
      ? extractValueFromLabel(value)
      : '';
}

type ConvertDocumentPropertyByInputTypeProps = {
  propertyPath?: string;
  documentCategory: Utils.ProductDocumentCategory;
  inputType: InputType;
  inputLabel: string;
  value: any;
  options: {
    isPackage?: boolean;
    isTaxExempt?: boolean;
    revision?: {
      exist?: boolean;
      number?: number;
    };
  };
};
export const convertDocumentPropertyByInputType = ({
  propertyPath,
  documentCategory,
  value,
  inputLabel,
  inputType,
  options,
}: ConvertDocumentPropertyByInputTypeProps) => {
  let propertyInputType = undefined;

  if (documentCategory === 'ProductDocument' || documentCategory === 'ProductEnhancementDocument')
    propertyInputType = propertyPath && productDocumentPropertyPathTypeMapping[propertyPath];
  else if (documentCategory === 'ProductRefundDocument')
    propertyInputType = propertyPath && productRefundDocumentPropertyPathTypeMapping[propertyPath];
  else if (documentCategory === 'ProductTransferOwnershipDocument')
    propertyInputType = propertyPath && productTransferOwnershipDocumentPropertyPathTypeMapping[propertyPath];

  if (value == null) return '';
  if (options.isPackage && packagePropertyPaths.some((it) => it === propertyPath)) {
    value = 'PKG';
  } else if (options.isTaxExempt && taxPropertyPaths.some((it) => it === propertyPath)) {
    value = 'EXEMPT';
  } else if (propertyInputType) {
    const isPropertyTypeDate = propertyInputType === InputType.date || propertyInputType === InputType.dateTime;
    const isPropertyTypeDifferent = inputType && inputType !== propertyInputType;
    if (typeof value === 'object') {
      value = Array.isArray(value)
        ? flatten(value.map(convertObjectToKeyValuePairList))
        : convertObjectToKeyValuePairList(value);
    }
    if (!isPropertyTypeDate && isPropertyTypeDifferent) {
      switch (inputType) {
        case InputType.boolean:
          value = checkIfValueExists(value, inputLabel);
          break;
        case InputType.number:
          value = checkIfValueExists(value, inputLabel) ? 1 : 0;
          break;
        case InputType.decimal:
          value = checkIfValueExists(value, inputLabel) ? 1 : 0;
          break;
        case InputType.text:
          if (typeof value === 'object') {
            const parsed = checkIfValueExists(value, inputLabel) ? getLabelFromValue(value, inputLabel) : '';
            value = parsed || '';
          } else {
            value = value?.toString();
          }
          break;
      }
    } else if (isPropertyTypeDate) {
      const d = dayjs(value, InputType.date ? defaultServerDateFormat : defaultServerDateTimeFormat);
      switch (inputType) {
        case InputType.year:
          value = d.format('YYYY');
          break;
        case InputType.month:
          value = d.format('MM');
          break;
        case InputType.day:
          value = d.format('DD');
          break;
        case InputType.number:
          value = d.valueOf();
          break;
        case InputType.decimal:
          value = d.valueOf();
          break;
        case InputType.date:
          value = d.format(defaultDateFormat);
          break;
        case InputType.dateTime:
          value = d.format(defaultDateTimeFormat);
          break;
      }
    }
  } else if ((inputType === InputType.month || inputType === InputType.day) && value.toString().length === 1) {
    value = `0${value}`;
  }
  if ((typeof value == 'number' || !isNaN(Number(value))) && InputType.price === inputType) {
    return formatNumberWithSeparator(value);
  } else if ((typeof value == 'number' || !isNaN(Number(value))) && InputType.formattedNumber === inputType) {
    return formatNumberWithSeparatorWithoutDecimales(Number(value));
  } else if ((typeof value == 'number' || !isNaN(Number(value))) && InputType.formattedDecimal === inputType) {
    return formatNumberWithSeparator(Number(value));
  }
  return typeof value === 'boolean' ? (value ? 'X' : '') : value?.toString() || '';
};
